import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSocketContextData } from 'src/context/socket'
import { useTranslation } from 'react-i18next'
import {
  Input,
  Button,
  Select,
  SelectItem,
  Card,
  CardBody,
  Progress,
  Chip,
  CircularProgress,
  Image,
} from '@nextui-org/react'
import { Icon } from '@iconify/react'
import dayjs from 'dayjs'
import countryObject from 'src/data/country_object.json'
import api from 'src/lib/api'
import Mixpanel from 'src/lib/mixpanel'
import { LanguageSelector } from 'src/components/i18n-selector'

interface FormData {
  // Step 1 - Website
  website: string

  // Step 2 - Company Info
  companyName: string
  industry: string
  region: string

  // Step 3 - Contact Info
  fullName: string
  workEmail: string
  role: string

  // Step 4 - Preferences
  contractFrequency: string
  contractSize: string
}

interface WebsiteProfile {
  website?: string
  name?: string
  description?: string
  productOffering?: string | string[]
  color?: string
  logo?: string
  logoUrl?: string
  i18n?: string
}

interface Tender {
  uuid: string
  title: string
  buyer: string
  score: number
  totalValue: number
  valueCurrency: string
  source: string
  country: string
  publicationDate: string
  deadline: string
  publicationNumber: string
  url: string
}

const getIndustries = (t: any) => [
  { value: 'it-services', label: t('industries.it-services') },
  { value: 'construction', label: t('industries.construction') },
  { value: 'logistics', label: t('industries.logistics') },
  { value: 'healthcare', label: t('industries.healthcare') },
  { value: 'education', label: t('industries.education') },
  { value: 'manufacturing', label: t('industries.manufacturing') },
  { value: 'consulting', label: t('industries.consulting') },
  { value: 'energy', label: t('industries.energy') },
  { value: 'telecommunications', label: t('industries.telecommunications') },
  { value: 'finance', label: t('industries.finance') },
  { value: 'other', label: t('industries.other') },
]

const getRoles = (t: any) => [
  { value: 'owner', label: t('roles.owner') },
  { value: 'sales', label: t('roles.sales') },
  { value: 'business-dev', label: t('roles.business-dev') },
  { value: 'other', label: t('roles.other') },
]

const getContractFrequencies = (t: any) => [
  { value: 'never', label: t('contract-frequency.never') },
  { value: 'occasionally', label: t('contract-frequency.occasionally') },
  { value: 'regularly', label: t('contract-frequency.regularly') },
]

const getContractSizes = (t: any) => [
  { value: 'small', label: t('contract-size.small-euro') },
  { value: 'medium', label: t('contract-size.medium-euro') },
  { value: 'large', label: t('contract-size.large-euro') },
  { value: 'xlarge', label: t('contract-size.xlarge-euro') },
]

const getCountriesWithPriority = () => {
  // Priority countries (Germany and Austria)
  const priorityCountries = [
    { code: 'DEU', name: 'Germany' },
    { code: 'AUT', name: 'Austria' },
  ]

  // All other countries (excluding problematic ones)
  const otherCountries = Object.entries(countryObject)
    .filter(([code, name]) => {
      // Remove problematic options that don't work
      const excludedCodes = ['SPCY']
      return !excludedCodes.includes(code)
    })
    .filter(([code, name]) => {
      // Remove priority countries from the main list
      return !['DEU', 'AUT'].includes(code)
    })
    .map(([code, name]) => ({ code, name }))
    .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically

  return { priorityCountries, otherCountries }
}

export default function TenderMatchingForm() {
  const navigate = useNavigate()
  const { io, isSocketConnected } = useSocketContextData()
  const { t } = useTranslation('tender-matching')

  // Add CSS animations for the purple gradient button
  React.useEffect(() => {
    const style = document.createElement('style')
    style.textContent = `
      @keyframes gradient-shift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      @keyframes shine {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
      .animate-shine {
        animation: shine 2s ease-in-out infinite;
      }
    `
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [])
  const [currentStep, setCurrentStep] = useState(1)
  const [formStartTime, setFormStartTime] = useState<Date | null>(null)
  
  // Track form start
  useEffect(() => {
    if (currentStep === 1 && !formStartTime) {
      const startTime = new Date()
      setFormStartTime(startTime)
      
      // Track form start
      Mixpanel.track('Tender Finder Form Started', {
        step: 1,
        timestamp: startTime.toISOString()
      })
    }
  }, [currentStep, formStartTime])

  const [formData, setFormData] = useState<FormData>({
    website: '',
    companyName: '',
    industry: '',
    region: '',
    fullName: '',
    workEmail: '',
    role: '',
    contractFrequency: '',
    contractSize: '',
  })
  const [errors, setErrors] = useState<Partial<FormData>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isScanning, setIsScanning] = useState(false)
  const [scanError, setScanError] = useState('')
  const [websiteProfile, setWebsiteProfile] = useState<WebsiteProfile | null>(null)
  const [isFormSaved, setIsFormSaved] = useState(false)
  const [sessionId, setSessionId] = useState<string>('')
  const [tenders, setTenders] = useState<Tender[]>([])
  const [totalTenders, setTotalTenders] = useState(0)
  const [showResults, setShowResults] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editableDescription, setEditableDescription] = useState('')
  const [editableServices, setEditableServices] = useState<string[]>([])
  const [isEditingProfile, setIsEditingProfile] = useState(false)
  const [shareFormId, setShareFormId] = useState<string>('')
  const [previewTenderCount, setPreviewTenderCount] = useState<number | null>(null)

  // Form persistence functions
  const FORM_STORAGE_KEY = 'tender-matching-form-data'
  const PROFILE_STORAGE_KEY = 'tender-matching-profile-data'

  const saveFormToStorage = async (formData: FormData, websiteProfile: WebsiteProfile | null) => {
    try {
      // Save to localStorage as backup
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData))
      if (websiteProfile) {
        localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(websiteProfile))
      }

      // Save to backend via Socket.IO if connected
      if (io && isSocketConnected && sessionId) {
        io.emit('form_progress_save', {
          sessionId,
          formData,
          websiteProfile,
          currentStep,
        })
      }

      setIsFormSaved(true)
      // Clear the saved indicator after 3 seconds
      setTimeout(() => setIsFormSaved(false), 3000)
    } catch (error) {
      console.error('Failed to save form data:', error)
    }
  }

  const loadFormFromStorage = (): { formData: FormData | null; websiteProfile: WebsiteProfile | null } => {
    try {
      const savedFormData = localStorage.getItem(FORM_STORAGE_KEY)
      const savedProfileData = localStorage.getItem(PROFILE_STORAGE_KEY)

      return {
        formData: savedFormData ? JSON.parse(savedFormData) : null,
        websiteProfile: savedProfileData ? JSON.parse(savedProfileData) : null,
      }
    } catch (error) {
      console.error('Failed to load form data:', error)
      return { formData: null, websiteProfile: null }
    }
  }

  const clearFormStorage = () => {
    try {
      localStorage.removeItem(FORM_STORAGE_KEY)
      localStorage.removeItem(PROFILE_STORAGE_KEY)
    } catch (error) {
      console.error('Failed to clear form data:', error)
    }
  }

  // Initialize session ID and Socket.IO listeners
  useEffect(() => {
    // Generate or retrieve session ID
    const existingSessionId = localStorage.getItem('form-session-id')
    if (existingSessionId) {
      setSessionId(existingSessionId)
    } else {
      const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      setSessionId(newSessionId)
      localStorage.setItem('form-session-id', newSessionId)
    }
  }, [])

  // Socket.IO event listeners
  useEffect(() => {
    if (!io) return

    const handleFormProgressSaved = (data: any) => {
      console.log('Form progress saved:', data)
    }

    const handleFormProgressLoaded = (data: any) => {
      console.log('Form progress loaded:', data)
      if (data.formData) {
        setFormData(data.formData)
      }
      if (data.websiteProfile) {
        setWebsiteProfile(data.websiteProfile)
      }
      if (data.currentStep) {
        setCurrentStep(data.currentStep)
      }
    }

    const handleFormProgressUpdated = (data: any) => {
      console.log('Form progress updated from another device:', data)
      // Optionally sync with other devices
    }

    const handleFormProgressError = (data: any) => {
      console.error('Form progress error:', data)
    }

    // Add event listeners
    io.on('form_progress_saved', handleFormProgressSaved)
    io.on('form_progress_loaded', handleFormProgressLoaded)
    io.on('form_progress_updated', handleFormProgressUpdated)
    io.on('form_progress_error', handleFormProgressError)

    // Cleanup listeners
    return () => {
      io.off('form_progress_saved', handleFormProgressSaved)
      io.off('form_progress_loaded', handleFormProgressLoaded)
      io.off('form_progress_updated', handleFormProgressUpdated)
      io.off('form_progress_error', handleFormProgressError)
    }
  }, [io])

  // Load saved form data on component mount
  useEffect(() => {
    const { formData: savedFormData, websiteProfile: savedProfile } = loadFormFromStorage()
    if (savedFormData) {
      setFormData(savedFormData)
      if (savedProfile) {
        setWebsiteProfile(savedProfile)
      }
    }
  }, [])

  // Load form progress from backend when socket connects
  useEffect(() => {
    if (io && isSocketConnected && sessionId) {
      io.emit('form_session_join', { sessionId })
      io.emit('form_progress_load', { sessionId })
    }
  }, [io, isSocketConnected, sessionId])

  // Initialize editable description and services when website profile is loaded
  useEffect(() => {
    if (websiteProfile?.description && !editableDescription) {
      setEditableDescription(websiteProfile.description)
    }

    if (websiteProfile?.productOffering && editableServices.length === 0) {
      let services: string[] = []
      if (Array.isArray(websiteProfile.productOffering)) {
        services = websiteProfile.productOffering
      } else if (typeof websiteProfile.productOffering === 'string') {
        services = websiteProfile.productOffering.split('\n').map((item) => item.replace(/^[•\-*]\s*/, ''))
      }
      setEditableServices(services)
    }
  }, [websiteProfile, editableDescription, editableServices])

  // Auto-save form data whenever it changes
  useEffect(() => {
    // Only save if we have meaningful data (not just the initial empty state)
    if (formData.website || formData.companyName || formData.fullName || formData.workEmail) {
      saveFormToStorage(formData, websiteProfile)
    }
  }, [formData, websiteProfile])

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<FormData> = {}

    if (step === 1) {
      if (!formData.website.trim()) newErrors.website = t('validation.website-required')
      else if (!isValidWebsite(formData.website)) newErrors.website = t('validation.website-invalid')
    } else if (step === 2) {
      if (!formData.companyName.trim()) newErrors.companyName = t('validation.company-name-required')
      if (!formData.industry) newErrors.industry = t('validation.industry-required')
      if (!formData.region) newErrors.region = t('validation.region-required')
    } else if (step === 3) {
      // Step 3 is now contract preferences (was step 4)
      if (!formData.contractFrequency) newErrors.contractFrequency = t('validation.contract-frequency-required')
      if (!formData.contractSize) newErrors.contractSize = t('validation.contract-size-required')
    } else if (step === 4) {
      // Step 4 is now contact information (was step 3)
      if (!formData.fullName.trim()) newErrors.fullName = t('validation.full-name-required')
      if (!formData.workEmail.trim()) {
        newErrors.workEmail = t('validation.work-email-required')
      } else if (!isValidWorkEmail(formData.workEmail)) {
        newErrors.workEmail = t('validation.work-email-invalid')
      }
      if (!formData.role) newErrors.role = t('validation.role-required')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const isValidWorkEmail = (email: string): boolean => {
    const personalDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'aol.com']
    const domain = email.split('@')[1]?.toLowerCase()
    return Boolean(domain && !personalDomains.includes(domain))
  }

  const isValidWebsite = (url: string): boolean => {
    try {
      // Add protocol if missing
      const urlWithProtocol = url.startsWith('http') ? url : `https://${url}`
      new URL(urlWithProtocol)
      return true
    } catch {
      return false
    }
  }

  const detectIndustryFromDescription = (description: string): string | null => {
    if (!description) return null

    const descriptionLower = description.toLowerCase()

    // Simple keyword-based industry detection
    if (
      descriptionLower.includes('software') ||
      descriptionLower.includes('development') ||
      descriptionLower.includes('programming')
    ) {
      return 'it-services'
    }
    if (
      descriptionLower.includes('construction') ||
      descriptionLower.includes('building') ||
      descriptionLower.includes('architecture')
    ) {
      return 'construction'
    }
    if (
      descriptionLower.includes('logistics') ||
      descriptionLower.includes('shipping') ||
      descriptionLower.includes('transport')
    ) {
      return 'logistics'
    }
    if (
      descriptionLower.includes('healthcare') ||
      descriptionLower.includes('medical') ||
      descriptionLower.includes('hospital')
    ) {
      return 'healthcare'
    }
    if (
      descriptionLower.includes('education') ||
      descriptionLower.includes('school') ||
      descriptionLower.includes('university')
    ) {
      return 'education'
    }
    if (
      descriptionLower.includes('manufacturing') ||
      descriptionLower.includes('production') ||
      descriptionLower.includes('factory')
    ) {
      return 'manufacturing'
    }
    if (
      descriptionLower.includes('consulting') ||
      descriptionLower.includes('advisory') ||
      descriptionLower.includes('strategy')
    ) {
      return 'consulting'
    }
    if (
      descriptionLower.includes('energy') ||
      descriptionLower.includes('power') ||
      descriptionLower.includes('electricity')
    ) {
      return 'energy'
    }
    if (
      descriptionLower.includes('telecommunications') ||
      descriptionLower.includes('telecom') ||
      descriptionLower.includes('communication')
    ) {
      return 'telecommunications'
    }
    if (
      descriptionLower.includes('finance') ||
      descriptionLower.includes('banking') ||
      descriptionLower.includes('financial')
    ) {
      return 'finance'
    }

    return null
  }

  const detectCountryFromWebsite = (website: string, description?: string): string | null => {
    if (!website) return null

    const websiteLower = website.toLowerCase()
    const descriptionLower = description?.toLowerCase() || ''

    // Check domain extensions
    if (websiteLower.includes('.de') || websiteLower.includes('germany') || descriptionLower.includes('germany')) {
      return 'DE'
    }
    if (websiteLower.includes('.fr') || websiteLower.includes('france') || descriptionLower.includes('france')) {
      return 'FR'
    }
    if (
      websiteLower.includes('.uk') ||
      websiteLower.includes('.co.uk') ||
      websiteLower.includes('united kingdom') ||
      descriptionLower.includes('united kingdom')
    ) {
      return 'GB'
    }
    if (
      websiteLower.includes('.nl') ||
      websiteLower.includes('netherlands') ||
      descriptionLower.includes('netherlands')
    ) {
      return 'NL'
    }
    if (websiteLower.includes('.it') || websiteLower.includes('italy') || descriptionLower.includes('italy')) {
      return 'IT'
    }
    if (websiteLower.includes('.es') || websiteLower.includes('spain') || descriptionLower.includes('spain')) {
      return 'ES'
    }
    if (websiteLower.includes('.be') || websiteLower.includes('belgium') || descriptionLower.includes('belgium')) {
      return 'BE'
    }
    if (websiteLower.includes('.at') || websiteLower.includes('austria') || descriptionLower.includes('austria')) {
      return 'AT'
    }
    if (
      websiteLower.includes('.ch') ||
      websiteLower.includes('switzerland') ||
      descriptionLower.includes('switzerland')
    ) {
      return 'CH'
    }
    if (websiteLower.includes('.dk') || websiteLower.includes('denmark') || descriptionLower.includes('denmark')) {
      return 'DK'
    }
    if (websiteLower.includes('.se') || websiteLower.includes('sweden') || descriptionLower.includes('sweden')) {
      return 'SE'
    }
    if (websiteLower.includes('.no') || websiteLower.includes('norway') || descriptionLower.includes('norway')) {
      return 'NO'
    }
    if (websiteLower.includes('.fi') || websiteLower.includes('finland') || descriptionLower.includes('finland')) {
      return 'FI'
    }
    if (websiteLower.includes('.pl') || websiteLower.includes('poland') || descriptionLower.includes('poland')) {
      return 'PL'
    }
    if (websiteLower.includes('.cz') || websiteLower.includes('czech') || descriptionLower.includes('czech')) {
      return 'CZ'
    }
    if (websiteLower.includes('.hu') || websiteLower.includes('hungary') || descriptionLower.includes('hungary')) {
      return 'HU'
    }
    if (websiteLower.includes('.ro') || websiteLower.includes('romania') || descriptionLower.includes('romania')) {
      return 'RO'
    }
    if (websiteLower.includes('.bg') || websiteLower.includes('bulgaria') || descriptionLower.includes('bulgaria')) {
      return 'BG'
    }
    if (websiteLower.includes('.hr') || websiteLower.includes('croatia') || descriptionLower.includes('croatia')) {
      return 'HR'
    }
    if (websiteLower.includes('.si') || websiteLower.includes('slovenia') || descriptionLower.includes('slovenia')) {
      return 'SI'
    }
    if (websiteLower.includes('.sk') || websiteLower.includes('slovakia') || descriptionLower.includes('slovakia')) {
      return 'SK'
    }
    if (websiteLower.includes('.lt') || websiteLower.includes('lithuania') || descriptionLower.includes('lithuania')) {
      return 'LT'
    }
    if (websiteLower.includes('.lv') || websiteLower.includes('latvia') || descriptionLower.includes('latvia')) {
      return 'LV'
    }
    if (websiteLower.includes('.ee') || websiteLower.includes('estonia') || descriptionLower.includes('estonia')) {
      return 'EE'
    }
    if (websiteLower.includes('.ie') || websiteLower.includes('ireland') || descriptionLower.includes('ireland')) {
      return 'IE'
    }
    if (websiteLower.includes('.pt') || websiteLower.includes('portugal') || descriptionLower.includes('portugal')) {
      return 'PT'
    }
    if (websiteLower.includes('.gr') || websiteLower.includes('greece') || descriptionLower.includes('greece')) {
      return 'GR'
    }
    if (websiteLower.includes('.cy') || websiteLower.includes('cyprus') || descriptionLower.includes('cyprus')) {
      return 'CY'
    }
    if (websiteLower.includes('.mt') || websiteLower.includes('malta') || descriptionLower.includes('malta')) {
      return 'MT'
    }
    if (
      websiteLower.includes('.lu') ||
      websiteLower.includes('luxembourg') ||
      descriptionLower.includes('luxembourg')
    ) {
      return 'LU'
    }

    return null
  }

  const detectRoleFromCompanyInfo = (companyName: string, description?: string): string | null => {
    if (!companyName) return null

    const nameLower = companyName.toLowerCase()
    const descriptionLower = description?.toLowerCase() || ''

    // Check for owner/CEO indicators
    if (
      nameLower.includes('owner') ||
      nameLower.includes('ceo') ||
      nameLower.includes('founder') ||
      descriptionLower.includes('owner') ||
      descriptionLower.includes('ceo') ||
      descriptionLower.includes('founder')
    ) {
      return 'owner'
    }

    // Check for sales indicators
    if (
      nameLower.includes('sales') ||
      nameLower.includes('business development') ||
      descriptionLower.includes('sales') ||
      descriptionLower.includes('business development')
    ) {
      return 'sales'
    }

    // Default to business development for most cases
    return 'business-dev'
  }

  const detectContractPreferences = (
    description?: string,
    productOffering?: string | string[],
  ): { frequency: string; size: string } => {
    const descriptionLower = description?.toLowerCase() || ''
    const offerings = Array.isArray(productOffering)
      ? productOffering.join(' ').toLowerCase()
      : (productOffering || '').toLowerCase()
    const combinedText = `${descriptionLower} ${offerings}`

    // Detect contract frequency based on company type and services
    let frequency = 'occasionally' // default

    if (
      combinedText.includes('startup') ||
      combinedText.includes('small business') ||
      combinedText.includes('new company')
    ) {
      frequency = 'never'
    } else if (
      combinedText.includes('enterprise') ||
      combinedText.includes('large') ||
      combinedText.includes('corporation')
    ) {
      frequency = 'regularly'
    }

    // Detect contract size based on company description
    let size = 'medium' // default

    if (combinedText.includes('startup') || combinedText.includes('small') || combinedText.includes('local')) {
      size = 'small'
    } else if (
      combinedText.includes('enterprise') ||
      combinedText.includes('multinational') ||
      combinedText.includes('global')
    ) {
      size = 'large'
    } else if (
      combinedText.includes('fortune') ||
      combinedText.includes('mega') ||
      combinedText.includes('conglomerate')
    ) {
      size = 'xlarge'
    }

    return { frequency, size }
  }

  const profileWebsite = async (website: string) => {
    try {
      setIsScanning(true)
      setScanError('')

      // Ensure website has protocol
      const websiteWithProtocol = website.startsWith('http') ? website : `https://${website}`

      const response = await api.post('/automate/profile', {
        website: websiteWithProtocol,
      })

      const profileData = response.data
      console.log('Website profile data:', profileData)
      console.log('Product offering type:', typeof profileData.productOffering)
      console.log('Product offering value:', profileData.productOffering)

      // Store the profile data
      setWebsiteProfile(profileData)

      // Auto-fill form fields with extracted data
      if (profileData) {
        const detectedCountry = detectCountryFromWebsite(websiteWithProtocol, profileData.description)
        const detectedRole = detectRoleFromCompanyInfo(profileData.name || '', profileData.description)
        const detectedContractPrefs = detectContractPreferences(profileData.description, profileData.productOffering)

        setFormData((prev) => ({
          ...prev,
          website: profileData.website || websiteWithProtocol,
          companyName: profileData.name || prev.companyName,
          // Auto-detect industry based on description if possible
          industry: detectIndustryFromDescription(profileData.description) || prev.industry,
          // Auto-detect country/region
          region: detectedCountry || prev.region,
          // Auto-detect role
          role: detectedRole || prev.role,
          // Auto-detect contract preferences
          contractFrequency: detectedContractPrefs.frequency || prev.contractFrequency,
          contractSize: detectedContractPrefs.size || prev.contractSize,
        }))
      }

      setIsScanning(false)
      return profileData
    } catch (error: any) {
      console.error('Website profiling error:', error)
      setScanError(error.message || t('messages.failed-to-analyze'))
      setIsScanning(false)
      throw error
    }
  }

  const handleNext = async () => {
    if (validateStep(currentStep)) {
      const stepData = getStepData(currentStep)
      
      // Track step completion
      Mixpanel.track('Tender Finder Step Completed', {
        step: currentStep,
        step_name: getStepName(currentStep),
        form_data: stepData,
        time_on_step: formStartTime ? Date.now() - formStartTime.getTime() : 0
      })

      if (currentStep === 1) {
        // Start website scanning with actual API call
        try {
          await profileWebsite(formData.website)
          setCurrentStep((prev) => prev + 1)
        } catch (error) {
          // Error is already handled in profileWebsite function
          console.error('Failed to profile website:', error)
        }
      } else if (currentStep === 2) {
        // Move to step 3 (contract preferences)
        setCurrentStep((prev) => prev + 1)
      } else if (currentStep === 3) {
        // Get preview tender count before moving to step 4 (contact info)
        await getPreviewTenderCount()
        setCurrentStep((prev) => prev + 1)
      } else {
        setCurrentStep((prev) => prev + 1)
      }
    }
  }

  const handlePrevious = () => {
    // Track step abandonment (going back)
    Mixpanel.track('Tender Finder Step Abandoned', {
      step: currentStep,
      step_name: getStepName(currentStep),
      action: 'previous_button'
    })
    
    setCurrentStep((prev) => prev - 1)
  }

  // Helper functions for tracking
  const getStepName = (step: number): string => {
    switch (step) {
      case 1: return 'Website Analysis'
      case 2: return 'Company Information'
      case 3: return 'Contract Preferences'
      case 4: return 'Contact Information'
      default: return 'Unknown Step'
    }
  }

  const getStepData = (step: number): any => {
    switch (step) {
      case 1:
        return {
          website: formData.website,
          website_profile_found: !!websiteProfile
        }
      case 2:
        return {
          company_name: formData.companyName,
          industry: formData.industry,
          region: formData.region
        }
      case 3:
        return {
          contract_frequency: formData.contractFrequency,
          contract_size: formData.contractSize
        }
      case 4:
        return {
          full_name: formData.fullName,
          work_email: formData.workEmail,
          role: formData.role
        }
      default:
        return {}
    }
  }

  const handleEditPreferences = () => {
    // Track preference editing
    Mixpanel.track('Tender Finder Preferences Edited', {
      action: 'edit_preferences',
      from_step: 'results',
      to_step: 2,
      tender_count: totalTenders
    })
    
    setShowResults(false)
    setCurrentStep(2) // Go back to step 2 (company preferences)
  }

  const getPreviewTenderCount = async () => {
    try {
      // Call the same API as the full search but only get the count
      const response = await api.post('/tenders/matching-form/find-tenders', {
        ...formData,
        websiteProfile,
        previewOnly: true, // Add a flag to indicate we only want the count
      })

      if (response.data.success) {
        setPreviewTenderCount(response.data.total)
      } else {
        // Fallback to a reasonable estimate if API fails
        setPreviewTenderCount(2500)
      }
    } catch (error) {
      console.error('Error getting preview tender count:', error)
      // Fallback to a reasonable estimate if API fails
      setPreviewTenderCount(2500)
    }
  }

  const handleSubmit = async () => {
    if (!validateStep(4)) return

    setIsSubmitting(true)
    setIsLoading(true)

    try {
      // Call the real tender matching API
      const response = await api.post('/tenders/matching-form/find-tenders', {
        ...formData,
        websiteProfile,
      })

      if (response.data.success) {
        setTenders(response.data.tenders)
        setTotalTenders(response.data.total)
        setIsLoading(false)
        setShowResults(true)

        // Calculate form completion time
        const completionTime = formStartTime ? Date.now() - formStartTime.getTime() : 0

        // Track form completion
        Mixpanel.track('Tender Finder Form Completed', {
          total_time_ms: completionTime,
          total_time_seconds: Math.round(completionTime / 1000),
          steps_completed: 4,
          tender_count: response.data.total
        })

        // Track lead conversion across all platforms
        Mixpanel.track('Tender Finder Lead', {
          company_name: formData.companyName,
          industry: formData.industry,
          region: formData.region,
          contract_frequency: formData.contractFrequency,
          contract_size: formData.contractSize,
          tender_count: response.data.total,
          lead_source: 'tender_finder_form',
          conversion_value: 1,
          form_completion_time_ms: completionTime
        })

        // Send Facebook standard "Lead" event
        if (window.fbq) {
          window.fbq('track', 'Lead', {
            content_name: 'Tender Finder Form',
            content_category: 'Lead Generation',
            value: 1,
            currency: 'USD',
            company_name: formData.companyName,
            industry: formData.industry,
            region: formData.region
          })
        }

        // Automatically send email with results
        await sendResultsEmail(response.data.tenders, response.data.total)

        // Clear saved form data after successful submission
        clearFormStorage()

        // Mark form as completed in backend
        if (io && isSocketConnected && sessionId) {
          io.emit('form_progress_complete', { sessionId })
        }
      } else {
        throw new Error(response.data.error || t('messages.failed-to-find-tenders-error'))
      }
    } catch (error) {
      console.error('Error finding tenders:', error)
      setIsLoading(false)
      // Show error message to user
      alert(t('messages.failed-to-find-tenders'))
    }
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()

      if (currentStep === 4) {
        // On the last step (contact info), submit the form
        handleSubmit()
      } else {
        // On other steps, go to next step
        handleNext()
      }
    }
  }

  // Handle description update
  const handleDescriptionChange = (newDescription: string) => {
    setEditableDescription(newDescription)
    if (websiteProfile) {
      setWebsiteProfile((prev) => (prev ? { ...prev, description: newDescription } : null))
    }
  }

  // Handle services editing
  const handleServiceChange = (index: number, newValue: string) => {
    const updatedServices = [...editableServices]
    updatedServices[index] = newValue
    setEditableServices(updatedServices)
    if (websiteProfile) {
      setWebsiteProfile((prev) => (prev ? { ...prev, productOffering: updatedServices } : null))
    }
  }

  const handleAddService = () => {
    const newServices = [...editableServices, '']
    setEditableServices(newServices)
    if (websiteProfile) {
      setWebsiteProfile((prev) => (prev ? { ...prev, productOffering: newServices } : null))
    }
  }

  const handleRemoveService = (index: number) => {
    const updatedServices = editableServices.filter((_, i) => i !== index)
    setEditableServices(updatedServices)
    if (websiteProfile) {
      setWebsiteProfile((prev) => (prev ? { ...prev, productOffering: updatedServices } : null))
    }
  }

  const toggleEditMode = () => {
    setIsEditingProfile(!isEditingProfile)
  }

  // Generate a unique form ID for sharing
  const generateFormId = () => {
    return `form_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  // Generate email content with shareable link
  const generateEmailContent = () => {
    const shareUrl = `${window.location.origin}/tenders-finder/${shareFormId}`
    const companyName = formData.companyName || 'Your Company'

    return {
      subject: `Tender Opportunities Found for ${companyName}`,
      body: `Hi,

We found some great tender opportunities that are relevant for ${companyName}:

${tenders
  .slice(0, 3)
  .map(
    (tender, index) =>
      `${index + 1}. ${tender.title}
   Buyer: ${tender.buyer}
   Value: ${formatValue(tender)}
   Deadline: ${dayjs(tender.deadline).format('MMM DD, YYYY')}
   Country: ${(countryObject as Record<string, string>)?.[tender.country?.toUpperCase()] || tender.country}
   `,
  )
  .join('\n')}

Total opportunities found: ${totalTenders}

You can view the full report and sign up for more opportunities here:
${shareUrl}

Best regards`,
    }
  }

  // Generate email content with actual data (for email sending)
  const generateEmailContentWithData = (tenderResults: Tender[], totalCount: number, formId: string) => {
    const shareUrl = `${window.location.origin}/tenders-finder/${formId}`
    const companyName = formData.companyName || 'Your Company'

    return {
      subject: `Tender Opportunities Found for ${companyName}`,
      body: `Hi,

We found some great tender opportunities that are relevant for ${companyName}:

${tenderResults
  .slice(0, 3)
  .map(
    (tender, index) =>
      `${index + 1}. ${tender.title}
   Buyer: ${tender.buyer}
   Value: ${formatValue(tender)}
   Deadline: ${dayjs(tender.deadline).format('MMM DD, YYYY')}
   Country: ${(countryObject as Record<string, string>)?.[tender.country?.toUpperCase()] || tender.country}
   `,
  )
  .join('\n')}

Total opportunities found: ${totalCount}

You can view the full report and sign up for more opportunities here:
${shareUrl}

Best regards`,
    }
  }

  // Automatically send results email
  const sendResultsEmail = async (tenderResults: Tender[], totalCount: number) => {
    try {
      // Generate form ID and save results for sharing
      const formId = generateFormId()
      setShareFormId(formId)

      // Save results to backend for sharing
      await api.post('/tenders/share-results', {
        formId,
        tenders: tenderResults.slice(0, 3), // Only share first 3 tenders
        totalTenders: totalCount,
        companyName: formData.companyName,
        websiteProfile,
        createdAt: new Date().toISOString(),
      })

      // Generate email content with the actual results data
      const emailContent = generateEmailContentWithData(tenderResults, totalCount, formId)

      // Send email via backend
      await api.post('/tenders/send-results-email', {
        to: formData.workEmail,
        subject: emailContent.subject,
        body: emailContent.body,
        formId,
        companyName: formData.companyName,
      })

      console.log('Results email sent successfully')
    } catch (error) {
      console.error('Error sending results email:', error)
      // Don't show error to user as this is automatic
    }
  }

  const formatValue = (tender: Tender) => {
    if (!tender.totalValue) return '-'
    return Intl.NumberFormat('de-DE', {
      currency: tender.valueCurrency,
      style: 'currency',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(tender.totalValue)
  }

  const renderStep1 = () => (
    <div className="space-y-8">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('step-1.title')}</h1>
        <p className="text-xl text-gray-600">{t('step-1.subtitle')}</p>
      </div>

      <div className="space-y-6">
        <Input
          label={t('step-1.title')}
          placeholder={t('step-1.placeholder')}
          value={formData.website}
          onChange={(e) => setFormData((prev) => ({ ...prev, website: e.target.value }))}
          onKeyDown={handleKeyDown}
          isInvalid={!!errors.website}
          errorMessage={errors.website}
          size="lg"
          variant="bordered"
          classNames={{
            input: 'text-lg',
            inputWrapper: 'h-16',
          }}
          startContent={<Icon icon="lucide:globe" className="text-gray-400" />}
        />

        <div className="text-center">
          <p className="text-sm text-gray-500 mb-4">{t('step-1.description')}</p>
        </div>
      </div>
    </div>
  )

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('step-2.title')}</h1>
        <p className="text-gray-600">{t('step-2.help-text')}</p>
      </div>

      {websiteProfile && (
        <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2 text-green-700">
              <Icon icon="lucide:check-circle" className="text-green-500" />
              <span className="text-sm font-medium">{t('messages.website-analyzed-success')}</span>
            </div>
            <button
              type="button"
              onClick={toggleEditMode}
              className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              title={isEditingProfile ? 'Save changes' : 'Edit profile'}
            >
              <Icon icon={isEditingProfile ? 'lucide:check' : 'lucide:edit'} className="w-4 h-4" />
            </button>
          </div>

          {isEditingProfile ? (
            // Editable Mode
            <div className="space-y-4">
              {/* Editable Company Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Description</label>
                <textarea
                  value={editableDescription}
                  onChange={(e) => handleDescriptionChange(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={3}
                  placeholder="Enter your company description..."
                />
              </div>

              {/* Editable Services */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Services & Products</label>
                  <button
                    type="button"
                    onClick={handleAddService}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                  >
                    <Icon icon="lucide:plus" className="w-3 h-3" />
                    <span>Add Service</span>
                  </button>
                </div>

                <div className="space-y-2">
                  {editableServices.map((service, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={service}
                        onChange={(e) => handleServiceChange(index, e.target.value)}
                        className="flex-1 p-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter service or product..."
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveService(index)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Icon icon="lucide:trash-2" className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            // Read-only Mode
            <div className="space-y-3">
              {/* Read-only Company Description */}
              {editableDescription && (
                <div>
                  <span className="text-sm font-medium text-gray-700 block mb-1">Company Description</span>
                  <p className="text-sm text-gray-600">{editableDescription}</p>
                </div>
              )}

              {/* Read-only Services */}
              {editableServices.length > 0 && (
                <div>
                  <span className="text-sm font-medium text-gray-700 block mb-2">Services & Products</span>
                  <ul className="list-disc list-inside space-y-1">
                    {editableServices.map((service, index) => (
                      <li key={index} className="text-sm text-gray-600">
                        {service}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        <Input
          label={t('step-2.company-name')}
          placeholder={t('step-2.company-name-placeholder')}
          value={formData.companyName}
          onChange={(e) => setFormData((prev) => ({ ...prev, companyName: e.target.value }))}
          onKeyDown={handleKeyDown}
          isInvalid={!!errors.companyName}
          errorMessage={errors.companyName}
          size="lg"
          variant="bordered"
          startContent={websiteProfile?.name ? <Icon icon="lucide:check" className="text-green-500" /> : undefined}
        />

        <Select
          label={t('step-2.industry')}
          placeholder={t('step-2.industry-placeholder')}
          selectedKeys={formData.industry ? [formData.industry] : []}
          onSelectionChange={(keys) => setFormData((prev) => ({ ...prev, industry: Array.from(keys)[0] as string }))}
          isInvalid={!!errors.industry}
          errorMessage={errors.industry}
          size="lg"
          variant="bordered"
          startContent={
            websiteProfile &&
            websiteProfile.description &&
            detectIndustryFromDescription(websiteProfile.description) ? (
              <Icon icon="lucide:check" className="text-green-500" />
            ) : undefined
          }
        >
          {getIndustries(t).map((industry) => (
            <SelectItem key={industry.value} value={industry.value}>
              {industry.label}
            </SelectItem>
          ))}
        </Select>

        <Select
          label={t('step-2.region')}
          placeholder={t('step-2.region-placeholder')}
          selectedKeys={formData.region ? [formData.region] : []}
          onSelectionChange={(keys) => setFormData((prev) => ({ ...prev, region: Array.from(keys)[0] as string }))}
          isInvalid={!!errors.region}
          errorMessage={errors.region}
          size="lg"
          variant="bordered"
          startContent={
            websiteProfile &&
            websiteProfile.description &&
            detectCountryFromWebsite(formData.website, websiteProfile.description) ? (
              <Icon icon="lucide:check" className="text-green-500" />
            ) : undefined
          }
        >
          {(() => {
            const { priorityCountries, otherCountries } = getCountriesWithPriority()
            return [
              // Priority countries (Germany and Austria)
              ...priorityCountries.map(({ code, name }) => (
                <SelectItem key={code} value={code}>
                  {name}
                </SelectItem>
              )),
              // Divider
              <SelectItem key="divider" value="divider" isDisabled className="opacity-50">
                <div className="flex items-center w-full">
                  <div className="flex-1 h-px bg-gray-300"></div>
                  <span className="px-2 text-xs text-gray-500">Other Countries</span>
                  <div className="flex-1 h-px bg-gray-300"></div>
                </div>
              </SelectItem>,
              // Other countries
              ...otherCountries.map(({ code, name }) => (
                <SelectItem key={code} value={code}>
                  {name}
                </SelectItem>
              )),
            ]
          })()}
        </Select>
      </div>
    </div>
  )

  const renderStep4ContactInfo = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('step-3.title')}</h1>
        <p className="text-gray-600 mb-4">{t('step-3.subtitle')}</p>

        {/* Dynamic tender count message */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-center space-x-2 text-green-700 mb-2">
            <Icon icon="lucide:sparkles" className="text-green-500" />
            <span className="font-semibold">Great news!</span>
          </div>
          <p className="text-gray-700 text-sm">
            We found{' '}
            <span className="font-bold text-green-600">
              {previewTenderCount ? previewTenderCount.toLocaleString() : 'thousands of'} tenders
            </span>{' '}
            that match your company profile. Fill out the contact information below and see the results{' '}
            <span className="font-bold text-blue-600">immediately</span>!
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <Input
          label={t('step-3.full-name')}
          placeholder={t('step-3.full-name-placeholder')}
          value={formData.fullName}
          onChange={(e) => setFormData((prev) => ({ ...prev, fullName: e.target.value }))}
          onKeyDown={handleKeyDown}
          isInvalid={!!errors.fullName}
          errorMessage={errors.fullName}
          size="lg"
          variant="bordered"
        />

        <Input
          label={t('step-3.work-email')}
          placeholder={t('step-3.work-email-placeholder')}
          type="email"
          value={formData.workEmail}
          onChange={(e) => setFormData((prev) => ({ ...prev, workEmail: e.target.value }))}
          onKeyDown={handleKeyDown}
          isInvalid={!!errors.workEmail}
          errorMessage={errors.workEmail}
          size="lg"
          variant="bordered"
          description={t('step-3.work-email-description')}
        />

        <Select
          label={t('step-3.role')}
          placeholder={t('step-3.role-placeholder')}
          selectedKeys={formData.role ? [formData.role] : []}
          onSelectionChange={(keys) => setFormData((prev) => ({ ...prev, role: Array.from(keys)[0] as string }))}
          isInvalid={!!errors.role}
          errorMessage={errors.role}
          size="lg"
          variant="bordered"
          startContent={
            websiteProfile &&
            websiteProfile.name &&
            detectRoleFromCompanyInfo(websiteProfile.name, websiteProfile.description) ? (
              <Icon icon="lucide:check" className="text-green-500" />
            ) : undefined
          }
        >
          {getRoles(t).map((role) => (
            <SelectItem key={role.value} value={role.value}>
              {role.label}
            </SelectItem>
          ))}
        </Select>
      </div>
    </div>
  )

  const renderStep3ContractPreferences = () => (
    <div className="space-y-6" onKeyDown={handleKeyDown} tabIndex={0}>
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('step-4.title')}</h1>
        <p className="text-gray-600">{t('step-4.subtitle')}</p>
      </div>

      <div className="space-y-6">
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">
              How often do you apply for public contracts today?
            </label>
            {websiteProfile &&
            websiteProfile.description &&
            detectContractPreferences(websiteProfile.description, websiteProfile.productOffering).frequency ? (
              <div className="flex items-center space-x-1 text-green-600">
                <Icon icon="lucide:check" className="text-green-500" />
                <span className="text-xs">{t('step-3.auto-detected')}</span>
              </div>
            ) : null}
          </div>
          <div className="mt-4 mb-4 flex flex-row items-center gap-4 wrap">
            {getContractFrequencies(t).map((frequency) => (
              <Card
                key={frequency.value}
                isPressable
                className={`cursor-pointer transition-all ${
                  formData.contractFrequency === frequency.value
                    ? 'border-2 border-blue-500 bg-blue-50'
                    : 'border border-gray-200 hover:border-gray-300'
                }`}
                onPress={() => setFormData((prev) => ({ ...prev, contractFrequency: frequency.value }))}
              >
                <CardBody className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-900">{frequency.label}</span>
                    {formData.contractFrequency === frequency.value && (
                      <Icon icon="lucide:check" className="text-blue-500" />
                    )}
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
          {errors.contractFrequency && <p className="text-red-500 text-sm mt-2">{errors.contractFrequency}</p>}
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">What size contracts are you interested in?</label>
            {websiteProfile &&
            websiteProfile.description &&
            detectContractPreferences(websiteProfile.description, websiteProfile.productOffering).size ? (
              <div className="flex items-center space-x-1 text-green-600">
                <Icon icon="lucide:check" className="text-green-500" />
                <span className="text-xs">{t('step-3.auto-detected')}</span>
              </div>
            ) : null}
          </div>
          <div className="mt-4 mb-4 flex flex-row items-center gap-4 wrap">
            {getContractSizes(t).map((size) => (
              <Card
                key={size.value}
                isPressable
                className={`cursor-pointer transition-all ${
                  formData.contractSize === size.value
                    ? 'border-2 border-blue-500 bg-blue-50'
                    : 'border border-gray-200 hover:border-gray-300'
                }`}
                onPress={() => setFormData((prev) => ({ ...prev, contractSize: size.value }))}
              >
                <CardBody className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-900">{size.label}</span>
                    {formData.contractSize === size.value && <Icon icon="lucide:check" className="text-blue-500" />}
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
          {errors.contractSize && <p className="text-red-500 text-sm mt-2">{errors.contractSize}</p>}
        </div>
      </div>
    </div>
  )

  const renderWebsiteScanning = () => (
    <div className="text-center space-y-8">
      <div className="space-y-4">
        <div className="relative">
          <div className="w-24 h-24 mx-auto bg-gradient-to-r from-green-500 to-blue-600 rounded-full flex items-center justify-center">
            <Icon icon="lucide:scan" className="text-white text-4xl animate-pulse" />
          </div>
          <div className="absolute inset-0 w-24 h-24 mx-auto bg-gradient-to-r from-green-500 to-blue-600 rounded-full animate-ping opacity-20"></div>
        </div>

        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-gray-900">{t('scanning.title')}</h2>
          <p className="text-gray-600">{t('scanning.subtitle')}</p>
          {scanError && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2 text-red-700">
                <Icon icon="lucide:alert-circle" className="text-red-500" />
                <span className="text-sm">{scanError}</span>
              </div>
              <Button
                size="sm"
                color="danger"
                variant="flat"
                className="mt-2"
                onPress={() => {
                  setScanError('')
                  handleNext()
                }}
              >
                {t('step-1.retry')}
              </Button>
            </div>
          )}
        </div>

        <div className="flex justify-center space-x-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></div>
          <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        </div>

        <div className="mt-8">
          <div className="bg-gray-100 rounded-lg p-4 max-w-md mx-auto">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Icon icon="lucide:globe" className="text-gray-400" />
              <span>{formData.website}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderAIThinking = () => (
    <div className="text-center space-y-8">
      <div className="space-y-4">
        <div className="relative">
          <div className="w-24 h-24 mx-auto bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
            <Icon icon="lucide:brain-circuit" className="text-white text-4xl animate-pulse" />
          </div>
          <div className="absolute inset-0 w-24 h-24 mx-auto bg-gradient-to-r from-blue-500 to-purple-600 rounded-full animate-ping opacity-20"></div>
        </div>

        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-gray-900">{t('ai.analyzing-title')}</h2>
          <p className="text-gray-600">{t('ai.analyzing-subtitle')}</p>
        </div>

        <div className="flex justify-center space-x-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        </div>
      </div>
    </div>
  )

  const renderLandingPage = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Language selector */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
          <div className="mb-8 flex justify-center">
            <Image
              src="/img/powerdocs-logo.png"
              alt={t('landing.logo-alt')}
              width={350}
              radius="none"
              className="cursor-pointer"
              onClick={() => navigate('/')}
            />
          </div>

          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">{t('title')}</h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">{t('subtitle')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                color="secondary"
                size="lg"
                className="px-8 py-6 text-lg font-semibold"
                onPress={() => setShowForm(true)}
                startContent={<Icon icon="lucide:search" />}
              >
                {t('navigation.find-tenders')}
              </Button>
              <Button
                variant="bordered"
                size="lg"
                className="px-8 py-6 text-lg"
                onPress={() => navigate('/tenders')}
                startContent={<Icon icon="lucide:eye" />}
              >
                {t('navigation.browse-all')}
              </Button>
            </div>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full opacity-10 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-gradient-to-tr from-purple-400 to-pink-400 rounded-full opacity-10 blur-3xl"></div>
      </div>

      {/* Features Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('landing.why-choose-title')}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">{t('landing.why-choose-subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <CardBody>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon icon="lucide:brain-circuit" className="text-blue-600 text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('ai.powered-matching-title')}</h3>
                <p className="text-gray-600">{t('ai.powered-matching-desc')}</p>
              </CardBody>
            </Card>

            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <CardBody>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon icon="lucide:clock" className="text-green-600 text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('features.save-time')}</h3>
                <p className="text-gray-600">{t('descriptions.save-time-desc')}</p>
              </CardBody>
            </Card>

            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <CardBody>
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon icon="lucide:target" className="text-purple-600 text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('features.higher-success-rate')}</h3>
                <p className="text-gray-600">{t('ai.focus-tenders-desc')}</p>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('landing.how-it-works-title')}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">{t('landing.how-it-works-subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('features.tell-about-company')}</h3>
              <p className="text-gray-600">{t('descriptions.tell-about-company-desc')}</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('features.ai-analyzes-profile')}</h3>
              <p className="text-gray-600">{t('ai.ai-processes-desc')}</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('ai.get-matches-title')}</h3>
              <p className="text-gray-600">{t('ai.get-matches-desc')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">10,000+</div>
              <div className="text-gray-600">{t('features.active-tenders')}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">50+</div>
              <div className="text-gray-600">{t('features.countries-covered')}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">95%+</div>
              <div className="text-gray-600">{t('ai.match-accuracy')}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">24/7</div>
              <div className="text-gray-600">{t('features.ai-monitoring')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('title')}</h2>
          <p className="text-xl text-blue-100 mb-8">{t('subtitle')}</p>
          <Button
            color="secondary"
            size="lg"
            className="px-8 py-6 text-lg font-semibold"
            onPress={() => setShowForm(true)}
            startContent={<Icon icon="lucide:arrow-right" />}
          >
            {t('navigation.find-tenders')}
          </Button>
        </div>
      </div>
    </div>
  )

  const renderResults = () => {
    // Ensure minimum score of 60%
    const processedTenders = tenders.map((tender) => ({
      ...tender,
      score: Math.max(tender.score || 0, 60),
    }))

    // Show only top 3 clearly, rest as blurry rows
    const topTenders = processedTenders.slice(0, 3)
    const remainingTenders = processedTenders.slice(3)

    return (
      <div className="space-y-8">
        {/* Edit Preferences Button - Top Left */}
        <div className="flex justify-start">
          <Button
            variant="bordered"
            size="sm"
            onPress={handleEditPreferences}
            startContent={<Icon icon="lucide:edit" />}
            className="text-gray-600 hover:text-gray-800 border-gray-300 hover:border-gray-400"
          >
            Edit Preferences
          </Button>
        </div>

        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('results.title')}</h1>
          <p className="text-gray-600">{t('results.subtitle')}</p>
          <div className="mt-4 text-sm text-gray-500">{t('results.total-found', { total: totalTenders })}</div>

          {/* Email Sent Confirmation */}
          <div className="mt-6">
            <div className="inline-flex items-center space-x-2 text-green-600 bg-green-50 px-4 py-2 rounded-lg">
              <Icon icon="lucide:mail" className="text-green-500" />
              <span className="text-sm font-medium">Results also sent to {formData.workEmail}<br />(check your spam folder)</span>
            </div>
          </div>
        </div>

        {/* Top 3 tenders - clear display */}
        <div className="space-y-4">
          {topTenders.map((tender, index) => (
            <Card
              key={tender.uuid}
              className="p-6 cursor-pointer hover:shadow-lg transition-all duration-200 max-w-2xl mx-auto w-full hover:scale-[1.02]"
              isPressable
              onPress={() => window.open(tender.url, '_blank')}
            >
              <CardBody>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-sm text-gray-500">#{tender.publicationNumber}</span>
                      <Chip color="secondary" variant="flat" size="sm">
                        {tender.source}
                      </Chip>
                      <Chip color="primary" variant="flat" size="sm">
                        #{index + 1} {t('features.match-number')}
                      </Chip>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 break-words">{tender.title}</h3>
                    <p className="text-gray-600 mb-2 break-words">
                      {t('tender.buyer')}: {tender.buyer}
                    </p>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-500">
                      <span>
                        {(countryObject as Record<string, string>)?.[tender.country?.toUpperCase()] || tender.country}
                      </span>
                      <span>
                        {t('tender.published')}: {dayjs(tender.publicationDate).format('MMM DD, YYYY')}
                      </span>
                      {tender.deadline && (
                        <span>
                          {t('tender.deadline')}: {dayjs(tender.deadline).format('MMM DD, YYYY')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-center sm:items-end gap-2 flex-shrink-0">
                    <CircularProgress
                      color={tender.score >= 80 ? 'secondary' : tender.score >= 60 ? 'warning' : 'success'}
                      showValueLabel={true}
                      size="lg"
                      value={tender.score}
                      className="mb-2"
                    />
                    <span className="text-lg font-semibold text-gray-900 text-center">{formatValue(tender)}</span>
                  </div>
                </div>
                <div className="flex items-center justify-center pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Icon icon="lucide:external-link" className="text-gray-400" />
                    <span>{t('results.click-to-view')}</span>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>

        {/* Blurred preview of remaining tenders */}
        {totalTenders > 0 && (
          <div className="space-y-2">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-gray-700">
                {t('results.more-tenders', { count: totalTenders })}
              </h3>
              <p className="text-sm text-gray-500">{t('results.signup-prompt', { total: totalTenders })}</p>
            </div>

            <div className="relative p-4">
              {remainingTenders.slice(0, 3).map((tender) => (
                <Card key={tender.uuid} className="relative overflow-hidden mb-2">
                  <CardBody className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-gray-400">#{tender.publicationNumber}</span>
                          <Chip color="default" variant="flat" size="sm" className="opacity-50">
                            {tender.source}
                          </Chip>
                        </div>
                        <h4 className="text-sm font-medium text-gray-600 break-words">{tender.title}</h4>
                        <p className="text-xs text-gray-400 break-words">
                          {t('tender.buyer')}: {tender.buyer}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <CircularProgress
                          color="default"
                          showValueLabel={true}
                          size="sm"
                          value={tender.score}
                          className="opacity-50"
                        />
                        <span className="text-sm font-medium text-gray-500">{formatValue(tender)}</span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              ))}

              {totalTenders > 3 && (
                <Card className="relative overflow-hidden mb-2">
                  <CardBody className="p-4">
                    <div className="text-center">
                      <div className="text-sm text-gray-500">+ {totalTenders - 3} more tenders...</div>
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* Glass blur overlay only on the cards, not the button */}
              <div className="absolute inset-0 bg-white/2 backdrop-blur-sm pointer-events-none"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-white/1 via-transparent to-white/1 pointer-events-none"></div>
            </div>

            <div className="text-center pt-4 pb-20">
              <Button
                color="secondary"
                size="lg"
                onPress={() => navigate('/signup')}
                startContent={<Icon icon="lucide:arrow-right" />}
                className="w-full"
              >
                {t('results.signup-button', { total: totalTenders })}
              </Button>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (isScanning) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          {/* Language selector */}
          <div className="mb-6 flex justify-end">
            <LanguageSelector />
          </div>
          {renderWebsiteScanning()}
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          {/* Language selector */}
          <div className="mb-6 flex justify-end">
            <LanguageSelector />
          </div>
          {renderAIThinking()}
        </div>
      </div>
    )
  }

  if (showResults) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto">
          {/* Language selector */}
          <div className="mb-6 flex justify-end">
            <LanguageSelector />
          </div>
          {renderResults()}
        </div>
      </div>
    )
  }

  if (showForm) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <Card className="p-8">
            <CardBody>
              {/* Language selector */}
              <div className="mb-6 flex justify-end">
                <LanguageSelector />
              </div>
              {/* Back button and save indicator */}
              <div className="mb-6 flex items-center justify-between">
                <Button
                  variant="light"
                  onPress={() => setShowForm(false)}
                  startContent={<Icon icon="lucide:arrow-left" />}
                  className="text-gray-600"
                >
                  {t('navigation.back-to-landing')}
                </Button>

                {/* Auto-save indicator */}
                {isFormSaved && (
                  <div className="flex items-center space-x-2 text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                    <Icon icon="lucide:check-circle" className="text-green-500" />
                    <span className="text-sm font-medium">{t('navigation.progress-saved')}</span>
                  </div>
                )}
              </div>

              {/* Progress bar */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-600">Step {currentStep} of 4</span>
                  <span className="text-sm text-gray-500">{Math.round((currentStep / 4) * 100)}%</span>
                </div>
                <Progress value={(currentStep / 4) * 100} color="secondary" className="w-full" />
              </div>

              {/* Form content */}
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3ContractPreferences()}
              {currentStep === 4 && renderStep4ContactInfo()}

              {/* Navigation buttons */}
              <div className="flex justify-between mt-8">
                <Button
                  variant="bordered"
                  onPress={handlePrevious}
                  isDisabled={currentStep === 1}
                  startContent={<Icon icon="lucide:chevron-left" />}
                >
                  {t('navigation.previous')}
                </Button>

                 {currentStep === 4 ? (
                   <Button
                     onPress={handleSubmit}
                     endContent={<Icon icon="lucide:arrow-right" />}
                     className="relative overflow-hidden font-bold text-white shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700 hover:from-purple-600 hover:via-purple-700 hover:to-purple-800 animate-pulse"
                     style={{
                       background: 'linear-gradient(45deg, #8b5cf6, #7c3aed, #6d28d9, #5b21b6)',
                       backgroundSize: '400% 400%',
                       animation: 'gradient-shift 3s ease infinite, pulse 2s ease-in-out infinite',
                     }}
                   >
                     <span className="relative z-10">See Results Now &gt;</span>
                     <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shine"></div>
                   </Button>
                 ) : (
                  <Button color="secondary" onPress={handleNext} endContent={<Icon icon="lucide:chevron-right" />}>
                    {currentStep === 1 ? t('step-1.scan-button') : t('navigation.next')}
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    )
  }

  return renderLandingPage()
}
